import { createPortal } from 'react-dom';

/**
 * COMBO LOUVER (KA-5245) — sub-phase A1.5: the *How to* popup.
 *
 * `combo_louver.how_to.tmpl` (49 lines) rendered VERBATIM — three ordered lists
 * that are the product spec in prose and the best single statement of the
 * feature's intended behaviour. Opened by `openComboLouverHowTo`
 * (accessory.selector.js:3374-3391) from the combo-only *How to* link, which is
 * the FIRST row of the dialog's left column (louver.selector.tmpl:18-26).
 *
 * ★ WHY `createPortal` TO `#ksdm-content` — NOT decoration, geometry.
 * `.popup-content.floating-popup` is `position: fixed`, but the selector is
 * mounted inside `#component_rating_mode` / `.rotate-card`, which carry the
 * board's card-flip `transform`. A transformed ancestor becomes the containing
 * block for `position: fixed` descendants (and `#component_rating_mode` is
 * `overflow: auto hidden`), so rendering this inline puts it ~490px off and clips
 * it. Legacy sidesteps the same problem the same way — `$('#ksdm-content')
 * .append(comboLouverHowToTemplate())` (:3382) — and both prior React how-to
 * ports do too (FilterSelector.tsx:2143-2227, FanSelector.tsx:3028-3062).
 * Measured legacy rect: `x=805 y=163 w=620 h=995`, `position: fixed`.
 *
 * ★ DRAG / RESIZE DEVIATION (deliberate — follows the shipped React precedent).
 * Legacy also calls jQuery-UI `.draggable()` and `.resizable({handles:
 * "n,e,s,w,ne,se,sw,nw"})` on `#combo-louver-how-to-floating-popup` (:3384-3387),
 * so the legacy popup carries `ui-draggable ui-draggable-handle ui-resizable` +
 * 8 resize handles. React's already-shipped filter how-to (FilterSelector.tsx)
 * and fan how-to (FanSelector.tsx:3017-3027) are NEITHER draggable NOR
 * resizable — an accepted deviation there — and this popup is their third
 * sibling, so it matches them instead of pulling jQuery-UI into React. The
 * `cursor: move` the stylesheet puts on the heading/body/buttons is therefore
 * cosmetic here, exactly as it already is on the other two.
 *
 * Toggle-to-close is the caller's (`openComboLouverHowTo` closes when
 * `.combo-louver-how-to-popup` already `.active`, :3377-3380); this component is
 * simply mounted while open, which is that. The CLOSE button is
 * `closeComboLouverHowTo` (:3393-3396).
 */
export const ComboLouverHowTo = ({ onClose }: { onClose: () => void }) =>
  createPortal(
    <div className="combo-louver-how-to-popup popup-wrapper active">
      <div className="popup-content floating-popup" id="combo-louver-how-to-floating-popup">
        <h2 className="popup-heading">
          <i className="icon-info" />
          How to Configure a Combo Louver
        </h2>
        <div className="popup-content-part combo-louver-how-to-body">
          <p>
            <b>When the new Combo Louver is added</b>
          </p>
          <ol>
            <li>
              The front, left side and right side louvers are checked by default. The user may elect
              to uncheck any two of the three louvers. One louver must remain at a minimum.
            </li>
            <li>
              The Air Stream is set to Supply Air, by default. The user may elect to change the Air
              Stream.
            </li>
            <li>
              If the selected Air Stream has an airflow value, the user may elect to change the
              airflow.
            </li>
            <li>
              If the selected Air Stream has no associated global airflow, then the user must input
              the airflow value.
            </li>
            <li>
              The Enclosure Depth will be set to the maximum allowed (by the CMS) by default. The
              user may elect to change the enclosure depth to a reduced value.
            </li>
            <li>
              The desired face velocity is set to 500 fpm by default. The user may elect to change
              the desired face velocity.
            </li>
            <li>The auto-size is checked.</li>
          </ol>
          <p>
            <b>When the auto-size is checked</b>
          </p>
          <p className="regular-font">
            Once all conditions listed in items 1-7 above are satisfied, the logic will calculate the
            size of the combo enclosure, as well as the sizes of the three louvers automatically,
            using the following methodology:
          </p>
          <ol>
            <li>
              The airway widths of the left side and right side louvers is calculated equally and to
              the maximum possible, taking into consideration the depth value and the louver frame
              flange.
            </li>
            <li>
              The airway height of the front, left side and right side louvers will be equally
              calculated to the maximum possible, taking into consideration the section airway
              height and the louver frame flange.
            </li>
            <li>
              The front louver airway width will be automatically calculated, taking into
              consideration the desired face velocity, ensuring that the resulting outputted (actual)
              face velocity is equal or slightly less than the desired face velocity.
            </li>
            <li>
              The combo enclosure width will be automatically calculated, taking into consideration
              the calculated front louver airway width and the louver frame flange.
            </li>
            <li>
              The (actual) face velocity is calculated based on the user defined airflow and the
              auto-sized louver airway dimensions.
            </li>
            <li>
              The floor of the combo louver will be leveled with the floor of the section and the top
              of the combo enclosure can never be higher than the top of the section airway height.
            </li>
          </ol>
          <p>
            <b>When the auto-size is unchecked (manual mode)</b>
          </p>
          <ol>
            <li>
              The user may elect to define the width, height and depth of the combo enclosure, all
              within the minimum and maximum limits set by the CMS.
            </li>
            <li>
              The airway width of the front louver may be defined by the user, up to the maximum
              possible, taking into consideration the combo enclosure width and the louver frame
              flange.
            </li>
            <li>
              The airway widths of the left side and right side louvers may be defined by the user up
              the maximum possible, taking into consideration the depth value and the louver frame
              flange.
            </li>
            <li>
              The airway height of the front, left side and right side louvers may be defined by the
              user up to the maximum possible, taking into consideration the section airway height
              and the louver frame flange.
            </li>
            <li>
              The (actual) face velocity is calculated based on the user defined airflow and user
              defined louver airway dimensions.
            </li>
            <li>
              The floor of the combo louver may be leveled with or higher than the floor of the
              section and the top of the combo enclosure may be higher than the top of the section
              airway height.
            </li>
          </ol>
        </div>
        <div className="popup-control-buttons">
          <button
            className="combo-louver-how-to-close"
            name="combo_louver_how_to_close"
            onClick={onClose}
          >
            CLOSE
          </button>
        </div>
      </div>
    </div>,
    // legacy target (:3382); `#ksdm-content` exists on the AHU board and on every
    // page rendering the shared header shell. document.body is the safety net.
    document.getElementById('ksdm-content') ?? document.body
  );

export default ComboLouverHowTo;
